import { useMemo } from 'react'

import '@interchain-ui/react/styles'
import { CHAIN_INFO_LIST } from '@/config'
import { ChainProvider as CosmosKitProvider } from '@cosmos-kit/react'
import { assets, chains } from 'chain-registry'

// We import Leap in separate packages because they added their MetaMask Snap to @cosmos-kit/leap
import { wallets as CosmostationWallet } from '@cosmos-kit/cosmostation'
import { wallets as KeplrWallet } from '@cosmos-kit/keplr'
// import { wallets as MetaMaskWallet } from '@cosmos-kit/cosmos-extension-metamask'
import { wallets as LeapExtensionWallet } from '@cosmos-kit/leap-extension'
import { wallets as LeapMobileWallet } from '@cosmos-kit/leap-mobile'
import { wallets as OmniWallet } from '@cosmos-kit/omni'
import { wallets as XdefiWallet } from '@cosmos-kit/xdefi'

import { env, fatal } from '@/utils'
import { accountParser } from '@/wallet-utils/parser'
import type { SignerOptions } from '@cosmos-kit/core'
import { registryTypes, aminoTypes } from '@/wallet-utils'

const LeapWallet = [...LeapExtensionWallet, ...LeapMobileWallet]

const wallets = [...KeplrWallet, ...XdefiWallet, ...LeapWallet, ...OmniWallet, ...CosmostationWallet]

export function ChainProvider({ children }: { children: React.ReactNode }) {
  const signerOptions: SignerOptions = {
    preferredSignType: () => {
      return 'direct'
    },

    stargate: () => {
      return { accountParser }
    },

    signingStargate: () => {
      return {
        registry: registryTypes,
        aminoTypes,
        accountParser
      }
    }
  }

  const chainList = useMemo(
    () =>
      Object.entries(CHAIN_INFO_LIST).map(([_, chainInfo]) => {
        const chain = chains.find((chain) => {
          // chainId would not work for different environments (Dockernet, Testnet).
          // Bech32 seems like a safe bet.
          //
          // A recent update for the `chain-registry` package inserts bitbadges into cosmoshub-4.
          // This fixes that.
          return chainInfo.chainName === 'Cosmos Hub'
            ? chain.bech32_prefix === chainInfo.bech32Config.bech32PrefixAccAddr && chain.chain_name !== 'bitbadges'
            : chain.bech32_prefix === chainInfo.bech32Config.bech32PrefixAccAddr
        })

        if (chain == null) {
          throw fatal(`Unable to find chain id ${chainInfo.chainId} from chain registry.`)
        }

        return {
          ...chain,
          chain_id: chainInfo.chainId,
          pretty_name: chainInfo.chainName,
          apis: {
            rpc: [
              {
                address: chainInfo.rpc,
                provider: 'Stride Edge Server'
              }
            ],
            rest: [
              {
                address: chainInfo.rest,
                provider: 'Stride Edge Server'
              }
            ]
          }
        }
      }),
    [CHAIN_INFO_LIST, chains]
  )

  const endpoints = useMemo(() => {
    return Object.fromEntries(
      chainList.map((chain) => {
        return [
          chain.chain_name,
          {
            rpc: [chain.apis.rpc[0].address],
            rest: [chain.apis.rest[0].address],
            isLazy: true
          }
        ]
      })
    )
  }, [chainList])

  return (
    <CosmosKitProvider
      chains={chainList}
      assetLists={assets}
      wallets={wallets}
      // @ts-expect-error It sounds like we need to get our chain-registry and
      // @chain-registry/types versions figured out.
      signerOptions={signerOptions}
      sessionOptions={{
        // We'll keep the session alive for as long as possible
        // This also avoids unintentional crashes for on-going flows
        duration: Infinity,
        // Without this, wallet connection crashes with this error:
        // injected.js:1 WalletConnect Core is already initialized. This is probably a mistake and can lead to unexpected behavior. Init() was called 16 times
        // MaxListenersExceededWarning: Possible EventEmitter memory leak detected. 11 heartbeat_pulse listeners added. Use emitter.setMaxListeners() to increase limit
        callback: () => {}
      }}
      walletConnectOptions={{
        signClient: { projectId: env('WC_PROJECT_ID', '') }
      }}
      endpointOptions={{ isLazy: true, endpoints }}
      logLevel={
        process.env.NODE_ENV === 'development' && env('COSMOS_KIT_LOGS_ENABLED') === 'true' ? 'DEBUG' : 'ERROR'
      }>
      {children}
    </CosmosKitProvider>
  )
}
